import { environment } from './environment';

/**
 * Overlays an optional `config/config.json` onto the compiled `environment`
 * object, in place, before the app module is evaluated (INTO-324).
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * `environment.prod.ts` is compiled INTO the bundle, so every environment-specific
 * URL used to be fixed at `nx build` time. That made the `prod-*` rehearsal
 * hostnames untestable with the artifact we actually ship: validating them would
 * have meant building a DIFFERENT bundle from the one that goes to production, so
 * the rehearsal stopped being evidence about the thing being deployed.
 *
 * Dockerfile.dist already protects the matching property one layer in — it packages
 * the CI artifact so "the container serves BYTE-FOR-BYTE what the IIS deploy
 * serves ... the two deployment paths cannot drift". This extends that past nginx:
 * one image, one set of bytes, and the environment supplies the URLs.
 *
 * ── THE FILE IS OPTIONAL, DELIBERATELY ──────────────────────────────────────
 * When it is absent the compiled values stand, so a bare `docker run`, a local
 * `nx serve` and the IIS deploy all behave exactly as they did before. In
 * Kubernetes it arrives as a ConfigMap mounted at /usr/share/nginx/html/config/ —
 * no entrypoint script, which matters because the pods run with
 * `readOnlyRootFilesystem: true` and could not write into the web root anyway.
 *
 * ── ⚠️ CALL THIS BEFORE IMPORTING AppModule, NOT MERELY BEFORE bootstrap ────
 * AppModule's decorator metadata reads `environment.AUTH0_*` and
 * `environment.BASE_API_URL` while the module is being EVALUATED (app.module.ts,
 * the AuthModule.forRoot(...) call, the BASE_API_URL provider and the Tolgee
 * i18n prefix). A static
 * `import` of AppModule is hoisted above every statement in main.ts, so it would
 * capture the compiled values before this function could ever run. That is why
 * main.ts awaits this and then reaches AppModule through a DYNAMIC `import()`.
 * Converting that back to a static import will silently restore the old baked-in
 * behaviour — the app will still work, it will just ignore its configuration.
 */

// `production` drives enableProdMode() and `version` identifies the artifact. A
// config file that could rewrite either would let a pod misreport which build it
// is running, so both stay compiled in and are refused from config.
const PROTECTED_KEYS: readonly string[] = ['production', 'version'];

export async function loadRuntimeConfig(): Promise<void> {
  let url: string;
  try {
    // Resolved against <base href> rather than the origin root, so this also works
    // where the app is served from a sub-path (orderapp lives under /order/).
    url = new URL('config/config.json', document.baseURI).toString();
  } catch {
    return;
  }

  let response: Response;
  try {
    response = await fetch(url, { cache: 'no-store' });
  } catch {
    // Network error or blocked request: the compiled defaults are a valid config.
    return;
  }

  // 404 is the normal "not mounted" case and must stay silent.
  if (!response.ok) {
    return;
  }

  // A successful status is NOT enough. nginx's SPA fallback answers unknown paths
  // with index.html, so a missing file can arrive as 200 text/html. Parsing is what
  // actually decides whether we were handed a config.
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    console.warn('[runtime-config] config.json is not JSON (SPA fallback?); using compiled defaults.');
    return;
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    console.warn('[runtime-config] config.json is not a JSON object; using compiled defaults.');
    return;
  }

  // `environment` is a plain mutable object and every consumer reads its properties
  // at use time, so assigning onto it here reaches all call sites -- `prePath`
  // alone has 139 of them -- without
  // touching any of them. The cast is only to index it by string key.
  const target = environment as unknown as Record<string, unknown>;
  const applied: string[] = [];

  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (PROTECTED_KEYS.includes(key)) {
      console.warn(`[runtime-config] ignoring protected key "${key}".`);
      continue;
    }
    // An unknown key is a typo: nothing reads it, so applying it would do nothing
    // while looking like it had worked. Say so instead of failing silently.
    if (!(key in target)) {
      console.warn(`[runtime-config] ignoring unknown key "${key}".`);
      continue;
    }
    // Catches the classic ConfigMap mistake of "false" (a non-empty, truthy string)
    // where a boolean is wanted -- SIGNALR_SKIP_NEGOTIATION is exactly that shape.
    if (typeof value !== typeof target[key]) {
      console.warn(
        `[runtime-config] ignoring "${key}": expected ${typeof target[key]}, got ${typeof value}.`,
      );
      continue;
    }
    target[key] = value;
    applied.push(key);
  }

  if (applied.length > 0) {
    console.info(`[runtime-config] applied: ${applied.join(', ')}`);
  }
}
